(() => {
  const initProductMedia = (root = document) => {
    root.querySelectorAll('.product').forEach((section) => {
      const mainImage = section.querySelector('[data-product-main-image] img');
      const thumbs = section.querySelectorAll('[data-product-thumb]');
      const dots = section.querySelectorAll('.product__dot');
      const mobileGallery = section.querySelector('.product__mobile-gallery');
      const mobileSlides = section.querySelectorAll('.product__mobile-slide');

      const setActiveMedia = (activeIndex) => {
        thumbs.forEach((item, index) => {
          const isActive = index === activeIndex;

          item.classList.toggle('product__thumb--active', isActive);

          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        dots.forEach((dot, index) => {
          dot.classList.toggle('product__dot--active', index === activeIndex);
        });
      };

      const initMobileGalleryDots = () => {
        if (!mobileGallery || mobileSlides.length === 0 || dots.length === 0 || mobileGallery.dataset.productGalleryReady === 'true') {
          return;
        }

        let scrollFrame = null;
        mobileGallery.dataset.productGalleryReady = 'true';

        mobileGallery.addEventListener(
          'scroll',
          () => {
            if (scrollFrame) {
              return;
            }

            scrollFrame = window.requestAnimationFrame(() => {
              const activeSlide = Array.from(mobileSlides).reduce(
                (closest, slide, index) => {
                  const distance = Math.abs(slide.offsetLeft - mobileGallery.scrollLeft);

                  if (distance < closest.distance) {
                    return { distance, index };
                  }

                  return closest;
                },
                { distance: Number.POSITIVE_INFINITY, index: 0 }
              );

              setActiveMedia(activeSlide.index);
              scrollFrame = null;
            });
          },
          { passive: true }
        );
      };

      initMobileGalleryDots();

      if (!mainImage || thumbs.length === 0) {
        return;
      }

      thumbs.forEach((thumb, index) => {
        if (thumb.dataset.productThumbReady !== 'true') {
          thumb.dataset.productThumbReady = 'true';

          thumb.addEventListener('click', (event) => {
            event.preventDefault();

            if (!thumb.dataset.mainSrc) {
              return;
            }

            mainImage.src = thumb.dataset.mainSrc;

            if (thumb.dataset.mainSrcset) {
              mainImage.srcset = thumb.dataset.mainSrcset;
            }

            if (thumb.dataset.mainAlt) {
              mainImage.alt = thumb.dataset.mainAlt;
            }

            setActiveMedia(index);
          });
        }
      });
    });
  };

  const initProductAddonActions = (root = document) => {
    const formatMoney = (cents) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(cents / 100);
    };

    const updateProductSubmit = (section) => {
      const submitButton = section.querySelector('[data-product-submit]');

      if (!submitButton) {
        return;
      }

      const label = submitButton.querySelector('[data-product-submit-label]');
      const star = submitButton.querySelector('[data-product-submit-star]');
      const price = submitButton.querySelector('[data-product-submit-price]');
      const tax = submitButton.querySelector('[data-product-submit-tax]');
      const addToCartLabel = submitButton.dataset.addToCartLabel || 'AJOUTER AU PANIER';
      const initialLabel = label ? label.dataset.initialLabel || label.textContent : '';
      const isAvailable = submitButton.dataset.productSubmitAvailable === 'true';
      const requiresAddons = submitButton.dataset.productSubmitRequiresAddons === 'true';
      const addonActions = Array.from(section.querySelectorAll('[data-addon-action]'));
      const stickyButtons = Array.from(section.querySelectorAll('[data-product-sticky-submit]'));

      const syncStickyButtons = () => {
        stickyButtons.forEach((stickyButton) => {
          const stickyLabel = stickyButton.querySelector('[data-product-sticky-label]');
          const stickyStar = stickyButton.querySelector('[data-product-sticky-star]');
          const stickyPrice = stickyButton.querySelector('[data-product-sticky-price]');
          const stickyTax = stickyButton.querySelector('[data-product-sticky-tax]');

          stickyButton.disabled = submitButton.disabled;

          if (stickyLabel && label) {
            stickyLabel.textContent = label.textContent;
          }

          if (stickyStar && star) {
            stickyStar.hidden = star.hidden;
          }

          if (stickyPrice && price) {
            stickyPrice.hidden = price.hidden;
            stickyPrice.textContent = price.textContent;
          }

          if (stickyTax) {
            stickyTax.hidden = tax ? tax.hidden : true;
          }
        });
      };

      if (!requiresAddons || addonActions.length === 0) {
        submitButton.disabled = !isAvailable;

        if (label) {
          label.textContent = addToCartLabel;
        }

        if (star) {
          star.hidden = false;
        }

        if (price) {
          price.hidden = false;
        }

        if (tax) {
          tax.hidden = false;
        }

        syncStickyButtons();
        return;
      }

      const total = addonActions.reduce((sum, action) => {
        const quantity = Number(action.dataset.addonQuantity || 0);
        const unitPrice = Number(action.dataset.addonPrice || 0);

        return sum + quantity * unitPrice;
      }, 0);
      const hasAddons = total > 0;

      submitButton.disabled = !hasAddons || !isAvailable;

      if (label) {
        if (!label.dataset.initialLabel) {
          label.dataset.initialLabel = label.textContent;
        }

        label.textContent = hasAddons ? addToCartLabel : label.dataset.initialLabel || initialLabel;
      }

      if (star) {
        star.hidden = !hasAddons;
      }

      if (price) {
        price.hidden = !hasAddons;
        price.textContent = formatMoney(total);
      }

      if (tax) {
        tax.hidden = !hasAddons;
      }

      syncStickyButtons();
    };

    const updateBundleProgress = (section) => {
      const bundleTrack = section.querySelector('[data-bundle-track]');

      if (!bundleTrack) {
        return;
      }

      const addedAddonCount = Array.from(section.querySelectorAll('[data-addon-action]')).reduce((sum, action) => {
        return sum + Number(action.dataset.addonQuantity || 0);
      }, 0);
      const progressByCount = [0, 33, 66, 100];
      const progress = progressByCount[Math.min(addedAddonCount, 3)];

      bundleTrack.style.setProperty('--product-bundle-progress', `${progress}%`);

      section.querySelectorAll('[data-bundle-step]').forEach((step) => {
        const threshold = Number(step.dataset.bundleStep || 0);
        const isActive = threshold > 0 && addedAddonCount >= threshold;
        const wasActive = step.dataset.bundleStepActive === 'true';

        step.dataset.bundleStepActive = String(isActive);

        if (isActive && !wasActive) {
          step.classList.remove('product__bundle-step--sparkle-active');
          void step.offsetWidth;
          step.classList.add('product__bundle-step--sparkle-active');
        }

        if (!isActive) {
          step.classList.remove('product__bundle-step--sparkle-active');
        }
      });
    };

    root.querySelectorAll('.product').forEach((section) => {
      section.querySelectorAll('[data-product-sticky-submit]').forEach((stickyButton) => {
        if (stickyButton.dataset.productStickyReady === 'true') {
          return;
        }

        stickyButton.dataset.productStickyReady = 'true';

        stickyButton.addEventListener('click', () => {
          const submitButton = section.querySelector('[data-product-submit]');

          if (!submitButton || submitButton.disabled) {
            return;
          }

          submitButton.click();
        });
      });

      section.querySelectorAll('[data-addon-action]').forEach((action) => {
        if (action.dataset.addonActionReady === 'true') {
          return;
        }

        const addButton = action.querySelector('[data-addon-add]');
        const quantityControl = action.querySelector('[data-addon-quantity]');
        const quantityValue = action.querySelector('[data-addon-quantity-value]');
        const decrementButton = action.querySelector('[data-addon-decrement]');
        const incrementButton = action.querySelector('[data-addon-increment]');

        if (!addButton || !quantityControl || !quantityValue || !decrementButton || !incrementButton) {
          return;
        }

        let quantity = Number(action.dataset.addonInitialQuantity || 0);
        const maxQuantity = 5;
        quantity = Math.min(maxQuantity, Math.max(0, quantity));
        action.dataset.addonActionReady = 'true';

        const render = () => {
          action.dataset.addonQuantity = String(quantity);
          quantityValue.textContent = quantity;
          incrementButton.disabled = quantity >= maxQuantity;

          if (quantity > 0) {
            addButton.hidden = true;
            quantityControl.hidden = false;
          } else {
            addButton.hidden = false;
            quantityControl.hidden = true;
          }

          updateBundleProgress(section);
          updateProductSubmit(section);
        };

        addButton.addEventListener('click', () => {
          quantity = 1;
          render();
        });

        incrementButton.addEventListener('click', () => {
          quantity = Math.min(maxQuantity, quantity + 1);
          render();
        });

        decrementButton.addEventListener('click', () => {
          quantity = Math.max(0, quantity - 1);
          render();
        });

        render();
      });
      updateBundleProgress(section);
      updateProductSubmit(section);
    });
  };

  const initProductUgcStories = (root = document) => {
    root.querySelectorAll('.product').forEach((section) => {
      if (section.dataset.productUgcStoriesReady === 'true') {
        return;
      }

      const modal = section.querySelector('[data-product-ugc-modal]');
      const modalPanel = section.querySelector('.product__ugc-modal-panel');
      const modalPlayer = section.querySelector('[data-product-ugc-player]');

      if (!modal || !modalPlayer) {
        return;
      }

      section.dataset.productUgcStoriesReady = 'true';

      const openStory = (videoId) => {
        const template = section.querySelector(`[data-product-ugc-template="${videoId}"]`);

        if (!template) {
          return;
        }

        modalPlayer.innerHTML = template.innerHTML;
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('product-ugc-story-open');

        const video = modalPlayer.querySelector('video');

        if (video) {
          video.currentTime = 0;
          video.play().catch(() => {});
        }

        if (modalPanel) {
          modalPanel.focus();
        }
      };

      const closeStory = () => {
        if (modal.hidden) {
          return;
        }

        const video = modalPlayer.querySelector('video');

        if (video) {
          video.pause();
        }

        modalPlayer.innerHTML = '';
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('product-ugc-story-open');
      };

      section.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-product-ugc-open]');
        const closeButton = event.target.closest('[data-product-ugc-close]');

        if (closeButton && section.contains(closeButton)) {
          event.preventDefault();
          closeStory();
          return;
        }

        if (openButton && section.contains(openButton)) {
          event.preventDefault();
          openStory(openButton.dataset.productUgcVideoId);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeStory();
        }
      });
    });
  };

  const initProductSection = (root = document) => {
    initProductMedia(root);
    initProductAddonActions(root);
    initProductUgcStories(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initProductSection());
  } else {
    initProductSection();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initProductSection(event.target);
  });
})();
